<script setup lang="ts">
import type { CreateLinkedAccount } from '~/types'

const props = defineProps<{
  context: {
    value?: string | string[]
    node: { input: (v?: string | (string | undefined)[]) => void }
    disabled?: boolean
    hint?: string
    multiple?: boolean
    translationKey?: string
  }
}>()

const formData = ref<CreateLinkedAccount | undefined>()
const isPopupOpen = ref(false)
const isLoading = ref(false)

const { $i18n } = useNuxtApp()
const { $trpc, queryClient, useMutation, makeTrpcErrorToast, makeTrpcSuccessToast } = useMutationHelpers()
const create = useMutation({
  mutationFn: $trpc.accounts.createLinkedAccount.mutate,
  onError: makeTrpcErrorToast({ entity: $i18n.t('account.entity'), mode: 'create' }, () => isLoading.value = false),
  onSuccess: async (res) => {
    await queryClient.invalidateQueries({ queryKey: ['accounts', 'getAll'] })
    await queryClient.invalidateQueries({ queryKey: ['persons', 'getAll'] })
    makeTrpcSuccessToast({ entity: $i18n.t('account.entity'), mode: 'create' })

    isPopupOpen.value = false
    isLoading.value = false

    if (!props.context.multiple) {
      return props.context.node.input(res?.id)
    }
    if (Array.isArray(props.context.value)) {
      if (res?.id && props.context.value.includes(res.id)) {
        return
      }
      return props.context.node.input([...props.context.value, res?.id])
    }
    return props.context.node.input([res?.id])
  },
})

const handleSubmit = () => {
  isLoading.value = true
  if (formData.value) {
    create.mutate(formData.value)
    formData.value = undefined
  }
}
</script>

<template>
  <div class="py-3 px-4 bg-primary-10 rounded">
    <div class="flex justify-between items-center">
      <template v-if="context.multiple && context.value">
        {{ $t(`person.creator.${context.translationKey ?? 'default'}.length`, context.value?.length || 0) }}
      </template>
      <template v-else-if="!context.multiple && context.value">
        {{ $t(`person.creator.${context.translationKey ?? 'default'}.length`, 1) }}
      </template>
      <template v-else>
        {{ $t(`person.creator.${context.translationKey ?? 'default'}.prompt`) }}
      </template>
      <div class="flex items-center gap-1" :class="context.value ? 'hidden md:flex' : ''">
        <n-button v-if="context.multiple || !context.value" type="primary" :disabled="context.disabled" @click="isPopupOpen = true">
          <template #icon>
            <AddIcon />
          </template>
          {{ $t(`person.creator.${context.translationKey ?? 'default'}.add`) }}
        </n-button>
      </div>
    </div>
    <LazyThePopup
      :title="$t('account.citizenPage.create')"
      :show="isPopupOpen"
      @close="isPopupOpen = false"
    >
      <div v-if="isLoading" class="grid place-items-center h-40">
        <n-spin />
      </div>
      <FormKitForm
        v-else
        v-model="formData"
        mode="create"
        @submit="handleSubmit"
        @cancel="isPopupOpen = false"
      >
        <PersonCreateOrEditInputs
          :person-fields="{ birthDate: true }"
        />
        <FormKit
          name="email"
          type="email"
          :label="$t('account.email.label')"
          :placeholder="$t('account.email.placeholder')"
          validation="required|email"
        />
      </FormKitForm>
    </LazyThePopup>
  </div>
  <p v-if="context.hint" class="text-xs text-gray-400 pt-1">
    {{ context.hint }}
  </p>
</template>
